import { thinkApi, taxSkillApi } from "../Api/Microservices";
import store from "../Redux/Store";
import { showError } from "../Redux/ErrorBoundary/ErrorBoundaryActions";
import { buildAppError } from "../Utils/AppError";
import {
  SourceDocument,
  AdminPanelAccess,
  SearchIndexFilterOption,
  TopicFilterOption,
  AffectedSearchIndex,
  ManageDocumentItem,
  ManageDocumentsPageResponse,
} from "../Models/Admin";

const SOURCE_DOCUMENT_BASE_PATH = "/sourcedocuments";

// Global admin-panel access check, called once after authentication.
// Deliberately does not dispatch a global error toast on failure: access is
// denied silently, since a permission check failing isn't a user-facing error.
export const AdminAccessService = {
  checkAccess: async (token: string): Promise<AdminPanelAccess> => {
    const response = await taxSkillApi.get<AdminPanelAccess>(
      "/check-admin-panel-access",
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },
};

export const SearchIndexFilterService = {
  getAll: async (token: string): Promise<SearchIndexFilterOption[]> => {
    try {
      const response = await taxSkillApi.get<SearchIndexFilterOption[]>(
        "/get-search-index-filter",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch Search Index list. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },
};

export const TopicFilterService = {
  getBySearchIndex: async (
    searchIndexId: string,
    token: string,
  ): Promise<TopicFilterOption[]> => {
    try {
      const response = await taxSkillApi.get<TopicFilterOption[]>(
        "/get-topic-filter-by-search-index",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { search_index_id: searchIndexId },
        },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch Topic list. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },
};

export interface BatchUploadResult {
  fileName: string;
  searchIndexId: string;
  topicId: string;
  sourceDocumentId: string;
}

export const AdminBatchUploadService = {
  upload: async (
    files: File[],
    searchIndexId: string,
    topicId: string,
    token: string,
  ): Promise<BatchUploadResult[]> => {
    try {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      form.append("searchIndexId", searchIndexId);
      form.append("topicId", topicId);

      const response = await taxSkillApi.post<BatchUploadResult[]>(
        "/batch-upload",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to upload documents. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },
};

export const MANAGE_DOCUMENTS_PAGE_SIZE = 100; // backend max (page_size: le=100)

export interface ManageDocumentsFilters {
  searchIndexId?: string | null;
  topicId?: string | null;
  status?: string | null;
  searchText?: string | null;
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc" | null;
}

export const ManageDocumentsService = {
  getProcessing: async (
    token: string,
  ): Promise<ManageDocumentsPageResponse> => {
    try {
      const response = await taxSkillApi.get<ManageDocumentsPageResponse>(
        "/admin/documents/processing",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return {
        ...response.data,
        Items: response.data.Items.map((item) => ({
          ...item,
          id: item.SourceDocumentId,
        })),
      };
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch processing documents. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  // Server-side pagination: fetches exactly one page (100 rows) per call,
  // so viewing a large document list doesn't require fetching everything
  // up front. Call again with the next/previous pageNumber on navigation.
  list: async (
    pageNumber: number,
    token: string,
    filters?: ManageDocumentsFilters,
  ): Promise<ManageDocumentsPageResponse> => {
    try {
      const params: Record<string, unknown> = {
        page_number: pageNumber,
        page_size: MANAGE_DOCUMENTS_PAGE_SIZE,
      };
      if (filters?.searchIndexId) params.search_index_ids = filters.searchIndexId;
      if (filters?.topicId) params.topic_ids = filters.topicId;
      if (filters?.status) params.status = filters.status;
      if (filters?.searchText) params.search_text = filters.searchText;
      if (filters?.sortColumn) params.sort_column = filters.sortColumn;
      if (filters?.sortDirection) params.sort_direction = filters.sortDirection;

      const response = await taxSkillApi.get<ManageDocumentsPageResponse>(
        "/admin/documents",
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      return {
        ...response.data,
        Items: response.data.Items.map((item) => ({
          ...item,
          id: item.SourceDocumentId,
        })),
      };
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch Document list. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  getAffectedSearchIndexes: async (
    documentIds: string[],
    action: string,
    token: string,
  ): Promise<AffectedSearchIndex[]> => {
    try {
      const response = await taxSkillApi.post<AffectedSearchIndex[]>(
        "/admin/documents/search-indexes",
        { document_ids: documentIds, action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch affected search indexes. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  action: async (
    documentIds: string[],
    action: string,
    token: string,
  ): Promise<void> => {
    try {
      await taxSkillApi.post(
        "/admin/documents/action",
        { document_ids: documentIds, action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to update document status. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

};

export const SourceDocumentService = {
  getAll: async (token: string): Promise<SourceDocument[]> => {
    try {
      const response = await thinkApi.get<SourceDocument[]>(
        SOURCE_DOCUMENT_BASE_PATH,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to fetch Source Document list. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  upload: async (
    file: File,
    displayName: string,
    token: string,
  ): Promise<SourceDocument> => {
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("displayName", displayName);

      const response = await thinkApi.post<SourceDocument>(
        SOURCE_DOCUMENT_BASE_PATH,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to upload Source Document. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  update: async (
    id: string,
    data: Pick<SourceDocument, "displayName" | "isActive">,
    token: string,
  ): Promise<SourceDocument> => {
    try {
      const response = await thinkApi.put<SourceDocument>(
        `${SOURCE_DOCUMENT_BASE_PATH}/${id}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to update Source Document. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },

  remove: async (id: string, token: string): Promise<void> => {
    try {
      await thinkApi.delete(`${SOURCE_DOCUMENT_BASE_PATH}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error: any) {
      const appError = buildAppError(
        error,
        "Failed to delete Source Document. Please try again.",
      );
      store.dispatch(showError(appError));
      throw error;
    }
  },
};
